import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { ChevronDown } from "lucide-react";
import { useState } from "react";

const Terms = () => {
  const [expandedSection, setExpandedSection] = useState<string | null>(null);

  const sections = [
    {
      id: "agreement",
      title: "Agreement to Terms",
      content: "These Terms and Conditions ('Terms') govern your use of OptiBiz Suite's website, applications, and services ('Service'). By accessing and using our Service, you agree to be bound by these Terms. If you do not agree with any part of these terms, you may not use the Service. We reserve the right to modify these terms at any time. Your continued use after changes means you accept the new terms."
    },
    {
      id: "license",
      title: "Use License",
      content: `You are granted a limited, non-exclusive, non-transferable license to access and use the Service for lawful purposes. You agree NOT to:

• Copy, modify, or distribute any content without permission
• Reverse engineer, decompile, or disassemble the Service
• Access the Service with automated tools or scripts
• Interfere with Service operations or security
• Attempt unauthorized access to user accounts or systems
• Use the Service for illegal or harmful activities
• Violate any applicable laws or regulations`
    },
    {
      id: "accounts",
      title: "User Accounts and Responsibilities",
      content: `When creating an account, you agree to:

• Provide accurate and complete information
• Maintain confidentiality of your password and account
• Notify us immediately of unauthorized access
• Accept responsibility for all account activities
• Not share account access with others
• Comply with all terms and conditions
• Update information as needed

You are solely responsible for account security and any misuse. We are not liable for unauthorized access to your account.`
    },
    {
      id: "content",
      title: "User-Generated Content",
      content: `You retain all rights to content you create, upload, or transmit through our Service. By submitting content, you grant OptiBiz Suite a non-exclusive, worldwide, royalty-free license to use, display, and distribute your content for Service operation and improvement.

You represent that you own or have rights to all content you submit and that it does not infringe any third-party rights. You agree that your content does not violate our Acceptable Use Policy and is not illegal, defamatory, or harmful.`
    },
    {
      id: "ip",
      title: "Intellectual Property Rights",
      content: `All content on the Service, including text, graphics, logos, images, code, and software, is the property of OptiBiz Suite or its licensors. These materials are protected by copyright, trademark, and other intellectual property laws.

You may not use, copy, modify, or distribute this intellectual property without explicit written permission. Unauthorized use is prohibited and may result in legal action.`
    },
    {
      id: "warranty",
      title: "Disclaimer of Warranties",
      content: `The Service is provided 'AS IS' without warranties of any kind, express or implied. OptiBiz Suite disclaims:

• Merchantability or fitness for a particular purpose
• Non-infringement of third-party rights
• Accuracy, completeness, or reliability of content
• Continuous or uninterrupted access
• Freedom from errors or harmful components

Use the Service at your own risk. OptiBiz Suite is not responsible for data loss, service interruptions, or any damages.`
    },
    {
      id: "liability",
      title: "Limitation of Liability",
      content: `To the fullest extent permitted by law, OptiBiz Suite shall not be liable for:

• Indirect, incidental, special, or consequential damages
• Loss of profits, revenue, data, or business opportunity
• Any damages exceeding the amount you paid us (if any)
• Third-party claims or actions
• Service interruptions or unavailability

Some jurisdictions do not allow liability limitations. In such cases, our liability is limited to the extent permitted by law.`
    },
    {
      id: "indemnification",
      title: "Indemnification",
      content: `You agree to indemnify and hold harmless OptiBiz Suite, its officers, directors, employees, and agents from any claims, damages, losses, or expenses arising from:

• Your violation of these Terms
• Your use of the Service
• Your user-generated content
• Your infringement of third-party rights
• Any illegal or unauthorized activity

You will defend us at your expense against any third-party claims and pay all resulting damages and legal fees.`
    },
    {
      id: "termination",
      title: "Termination",
      content: `We may terminate or suspend your account immediately, without prior notice, if you:

• Violate these Terms or our policies
• Engage in illegal or harmful activities
• Breach security or abuse the Service
• Fail to pay required fees

Upon termination, your access ceases immediately. Data may be deleted unless required for legal reasons. Termination does not release you from obligations.`
    },
    {
      id: "modifications",
      title: "Modifications to Service",
      content: `OptiBiz Suite reserves the right to:

• Modify, suspend, or discontinue any part of the Service
• Change features, functionality, or pricing
• Remove content or restrict access
• Perform maintenance or updates

We are not liable for any modifications or discontinuation. Your continued use after changes indicates acceptance of the modifications.`
    },
    {
      id: "fees",
      title: "Fees and Payment",
      content: `Some Service features require payment. You agree to:

• Pay all stated fees in full and on time
• Maintain accurate billing information
• Accept price changes with 30 days' notice
• Not dispute charges after 60 days
• Allow automatic renewal unless cancelled

Refunds are not provided except where required by law. All fees are non-refundable unless explicitly stated otherwise.`
    },
    {
      id: "governing",
      title: "Governing Law and Jurisdiction",
      content: `These Terms are governed by the laws of Kenya, without regard to conflicts of law principles. Any disputes shall be resolved in the courts of Eldoret, Uasin Gishu County, Kenya.

You agree to submit to the jurisdiction of these courts and waive any objections based on inconvenient forum. Both parties agree to pursue local remedies before international arbitration.`
    },
    {
      id: "contact",
      title: "Contact Information",
      content: `For questions about these Terms, contact:

OptiBiz Suite
Email: info@optibiz.co.ke
Phone: +254 769 653 431
Address: Daima Towers, Eldoret, Uasin Gishu, Kenya

We respond to legal inquiries within 14 business days. Include 'Terms Inquiry' in your subject line for faster processing.`
    }
  ];

  return (
    <div className="min-h-screen bg-tasktide-bg dark:bg-gray-900 font-sans flex flex-col">
      <Header />

      <main className="flex-1">
        {/* Hero Section */}
        <section className="pt-32 pb-12 px-4 md:px-8 bg-gradient-to-b from-optibiz-orange/8 to-transparent dark:from-optibiz-orange/10">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl md:text-6xl font-extrabold text-tasktide-dark dark:text-white mb-6">
              Terms and Conditions
            </h1>
            <p className="text-xl text-gray-600 dark:text-gray-300 mb-4">
              Please read these terms carefully before using our service. Your use indicates acceptance.
            </p>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Last updated: December 2025 • Effective from December 1, 2025
            </p>
          </div>
        </section>

        {/* Content Section */}
        <section className="py-20 px-4 md:px-8">
          <div className="max-w-4xl mx-auto">
            <div className="space-y-4">
              {sections.map((section) => (
                <div
                  key={section.id}
                  className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden hover:border-optibiz-orange/30 dark:hover:border-optibiz-orange/50 transition-colors"
                >
                  <button
                    onClick={() =>
                      setExpandedSection(
                        expandedSection === section.id ? null : section.id
                      )
                    }
                    className="w-full px-6 py-5 flex items-center justify-between hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors text-left"
                  >
                    <h2 className="text-xl font-bold text-tasktide-dark dark:text-white">
                      {section.title}
                    </h2>
                    <ChevronDown
                      className={`w-6 h-6 text-optibiz-orange transition-transform duration-300 ${
                        expandedSection === section.id ? "rotate-180" : ""
                      }`}
                    />
                  </button>

                  {expandedSection === section.id && (
                    <div className="px-6 pb-6 border-t border-gray-200 dark:border-gray-700 pt-4">
                      <p className="text-gray-600 dark:text-gray-300 leading-relaxed whitespace-pre-wrap">
                        {section.content}
                      </p>
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* CTA Box */}
            <div className="mt-12 bg-optibiz-orange/10 dark:bg-optibiz-orange/20 rounded-xl p-8 border border-optibiz-orange/20 dark:border-optibiz-orange/30">
              <h3 className="text-2xl font-bold text-tasktide-dark dark:text-white mb-4">
                Questions About These Terms?
              </h3>
              <p className="text-gray-600 dark:text-gray-300 mb-6">
                Contact our legal team with any questions or concerns about these terms and conditions.
              </p>
              <a
                href="mailto:info@optibiz.co.ke"
                className="inline-block bg-optibiz-orange text-white px-8 py-3 rounded-lg font-semibold hover:bg-optibiz-orange/90 transition-all"
              >
                Contact Legal Team
              </a>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default Terms;
